// `PDFFile` (SuiteCompile/PDFFile.swift): a small reader and writer for classic PDF files (a cross-reference table,
// no object or cross-reference streams), enough to turn Typst's print-ready output into PDF/X and to check it. Stream
// data is copied untouched; only dictionaries are parsed.
//
// Beyond the Swift: `{ streams: true }` also reads cross-reference and object streams (Typst's ordinary PDF 1.7), for
// print preflight's box and font checks, which the Mac makes through CoreGraphics. Such a file can't be rewritten.
import { inflateSync } from 'fflate';

export class PDFFailure extends Error {
  readonly kind: 'malformed' | 'unsupported';
  constructor(kind: 'malformed' | 'unsupported', what: string) {
    super(kind === 'malformed' ? `The PDF couldn't be read: ${what}.` : `The PDF uses ${what}, which this step can't handle.`);
    this.kind = kind;
  }
}
const malformed = (what: string) => new PDFFailure('malformed', what);
const unsupported = (what: string) => new PDFFailure('unsupported', what);

export type PDFValue =
  | { t: 'null' }
  | { t: 'bool'; v: boolean }
  | { t: 'int'; v: number }
  | { t: 'real'; v: number }
  | { t: 'name'; v: string }
  | { t: 'string'; bytes: Uint8Array; hex: boolean }
  | { t: 'array'; v: PDFValue[] }
  | { t: 'dict'; v: PDFDict }
  | { t: 'ref'; n: number; g: number };

export const V = {
  null: { t: 'null' } as PDFValue,
  int: (v: number): PDFValue => ({ t: 'int', v }),
  real: (v: number): PDFValue => ({ t: 'real', v }),
  name: (v: string): PDFValue => ({ t: 'name', v }),
  array: (v: PDFValue[]): PDFValue => ({ t: 'array', v }),
  dict: (v: PDFDict): PDFValue => ({ t: 'dict', v }),
  ref: (n: number, g = 0): PDFValue => ({ t: 'ref', n, g }),
  string: (bytes: Uint8Array, hex: boolean): PDFValue => ({ t: 'string', bytes, hex }),
  /** `Value.text`: ASCII as a literal string, anything else as UTF-16BE with a BOM (hex). */
  text(s: string): PDFValue {
    if ([...s].every((c) => c.codePointAt(0)! < 128)) return { t: 'string', bytes: new TextEncoder().encode(s), hex: false };
    const units = [0xfe, 0xff];
    for (let i = 0; i < s.length; i++) { const u = s.charCodeAt(i); units.push(u >> 8, u & 0xff); }
    return { t: 'string', bytes: new Uint8Array(units), hex: true };
  },
};

export const asDict = (v: PDFValue | null | undefined) => (v?.t === 'dict' ? v.v : null);
export const asArray = (v: PDFValue | null | undefined) => (v?.t === 'array' ? v.v : null);
export const asName = (v: PDFValue | null | undefined) => (v?.t === 'name' ? v.v : null);
export const asNumber = (v: PDFValue | null | undefined) => (v?.t === 'int' || v?.t === 'real' ? v.v : null);
/** `Value.text`: a string's text (UTF-16BE with a BOM, else UTF-8). */
export function asText(v: PDFValue | null | undefined): string | null {
  if (v?.t !== 'string') return null;
  const b = v.bytes;
  if (b.length >= 2 && b[0] === 0xfe && b[1] === 0xff) {
    let s = '';
    for (let i = 2; i + 1 < b.length; i += 2) s += String.fromCharCode((b[i] << 8) | b[i + 1]);
    return s;
  }
  return new TextDecoder().decode(b);
}

/** A dictionary that keeps its keys in order, so rewritten objects read like the originals. */
export class PDFDict {
  private map = new Map<string, PDFValue>();
  constructor(pairs: [string, PDFValue][] = []) { for (const [k, v] of pairs) this.set(k, v); }
  get(key: string): PDFValue | undefined { return this.map.get(key); }
  set(key: string, value: PDFValue | undefined | null): void {
    if (value === undefined || value === null) this.map.delete(key);
    else this.map.set(key, value);
  }
  has(key: string): boolean { return this.map.has(key); }
  get keys(): string[] { return [...this.map.keys()]; }
  get pairs(): [string, PDFValue][] { return [...this.map.entries()]; }
  clone(): PDFDict { return new PDFDict(this.pairs); }
}

export interface PDFObject {
  value: PDFValue;
  /** The stream's bytes as stored (still encoded), when the object is a stream. */
  stream: Uint8Array | null;
}

const ascii = (s: string) => new TextEncoder().encode(s);
const isWhite = (c: number) => c === 0x20 || c === 0x0a || c === 0x0d || c === 0x09 || c === 0x0c || c === 0x00;
const DELIMITERS = new Set([...'()<>[]{}/%'].map((c) => c.charCodeAt(0)));
const isDelimiter = (c: number) => DELIMITERS.has(c);

export class PDFLexer {
  constructor(readonly bytes: Uint8Array, public index: number) {
    this.index = Math.min(Math.max(index, 0), bytes.length);
  }

  static isWhite = isWhite;
  static isDelimiter = isDelimiter;

  skipWhitespace(): void {
    this.index = Math.min(Math.max(this.index, 0), this.bytes.length);
    const b = this.bytes;
    while (this.index < b.length) {
      if (isWhite(b[this.index])) this.index++;
      else if (b[this.index] === 0x25) { while (this.index < b.length && b[this.index] !== 0x0a && b[this.index] !== 0x0d) this.index++; }
      else break;
    }
  }

  keyword(): string {
    this.skipWhitespace();
    const start = this.index;
    const b = this.bytes;
    while (this.index < b.length && !isWhite(b[this.index]) && !isDelimiter(b[this.index])) this.index++;
    return latin(b.subarray(start, this.index));
  }

  value(): PDFValue | null {
    this.skipWhitespace();
    const b = this.bytes;
    if (this.index >= b.length) return null;
    const c = b[this.index];
    if (c === 0x2f) { // '/'
      this.index++;
      const start = this.index;
      while (this.index < b.length && !isWhite(b[this.index]) && !isDelimiter(b[this.index])) this.index++;
      const name: number[] = [];
      for (let i = start; i < this.index;) {
        if (b[i] === 0x23 && i + 2 < this.index) {
          const v = parseInt(latin(b.subarray(i + 1, i + 3)), 16);
          if (!Number.isNaN(v) && /^[0-9a-fA-F]{2}$/.test(latin(b.subarray(i + 1, i + 3)))) { name.push(v); i += 3; continue; }
        }
        name.push(b[i]); i++;
      }
      return V.name(new TextDecoder().decode(new Uint8Array(name)));
    }
    if (c === 0x28) { // '('
      this.index++;
      let depth = 1;
      const out: number[] = [];
      while (this.index < b.length) {
        const x = b[this.index++];
        if (x === 0x5c && this.index < b.length) {
          const e = b[this.index++];
          switch (e) {
            case 0x6e: out.push(0x0a); break;
            case 0x72: out.push(0x0d); break;
            case 0x74: out.push(0x09); break;
            case 0x62: out.push(0x08); break;
            case 0x66: out.push(0x0c); break;
            case 0x0a: break;
            case 0x0d: if (this.index < b.length && b[this.index] === 0x0a) this.index++; break;
            default:
              if (e >= 0x30 && e <= 0x37) {
                let octal = e - 48;
                for (let k = 0; k < 2 && this.index < b.length && b[this.index] >= 48 && b[this.index] <= 55; k++) octal = octal * 8 + (b[this.index++] - 48);
                out.push(octal & 0xff);
              } else out.push(e);
          }
        } else if (x === 0x28) { depth++; out.push(x); }
        else if (x === 0x29) { depth--; if (depth === 0) break; out.push(x); }
        else out.push(x);
      }
      return V.string(new Uint8Array(out), false);
    }
    if (c === 0x3c) { // '<'
      if (this.index + 1 < b.length && b[this.index + 1] === 0x3c) {
        this.index += 2;
        const dict = new PDFDict();
        for (;;) {
          this.skipWhitespace();
          if (this.index >= b.length) throw malformed('an unterminated dictionary');
          if (b[this.index] === 0x3e) { this.index += 2; return V.dict(dict); }
          const key = this.value();
          if (key?.t !== 'name') throw malformed("a dictionary key that isn't a name");
          dict.set(key.v, this.value() ?? V.null);
        }
      }
      this.index++;
      let hex = '';
      while (this.index < b.length && b[this.index] !== 0x3e) {
        if (!isWhite(b[this.index])) hex += String.fromCharCode(b[this.index]);
        this.index++;
      }
      this.index++;
      if (hex.length % 2 === 1) hex += '0';
      const bytes: number[] = [];
      for (let i = 0; i < hex.length; i += 2) {
        const pair = hex.slice(i, i + 2);
        if (/^[0-9a-fA-F]{2}$/.test(pair)) bytes.push(parseInt(pair, 16));
      }
      return V.string(new Uint8Array(bytes), true);
    }
    if (c === 0x5b) { // '['
      this.index++;
      const items: PDFValue[] = [];
      for (;;) {
        this.skipWhitespace();
        if (this.index >= b.length) throw malformed('an unterminated array');
        if (b[this.index] === 0x5d) { this.index++; return V.array(items); }
        const item = this.value();
        if (!item) throw malformed('an unterminated array');
        items.push(item);
      }
    }
    const word = this.keyword();
    switch (word) {
      case 'true': return { t: 'bool', v: true };
      case 'false': return { t: 'bool', v: false };
      case 'null': return V.null;
      case '': throw malformed('an unexpected character');
    }
    if (/^[+-]?\d+$/.test(word)) {
      const i = Number(word);
      // "n g R" is a reference. Plain tokens only (no recursion through long arrays).
      const save = this.index;
      const g = this.keyword();
      if (/^[+-]?\d+$/.test(g) && this.keyword() === 'R') return V.ref(i, Number(g));
      this.index = save;
      return V.int(i);
    }
    if (/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(word)) return V.real(Number(word));
    return V.name(word); // an operator or keyword where a value was expected; callers check
  }
}

function latin(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return s;
}

function lastIndexOf(keyword: string, bytes: Uint8Array): number | null {
  const k = ascii(keyword);
  const floor = Math.max(0, bytes.length - 4096);
  outer: for (let i = bytes.length - k.length; i >= floor; i--) {
    for (let j = 0; j < k.length; j++) if (bytes[i + j] !== k[j]) continue outer;
    return i;
  }
  return null;
}

/** Where an object lives: at a byte offset, or inside an object stream (cross-reference streams only). */
type Location = { offset: number } | { stream: number; index: number };

export class PDFFile {
  readonly bytes: Uint8Array;
  readonly version: string;
  /** Object number → byte offset of "n g obj" (objects stored directly). */
  readonly offsets: Map<number, number>;
  /** Objects inside object streams (a cross-reference stream's files only). */
  readonly compressed = new Map<number, { stream: number; index: number }>();
  readonly trailer: PDFDict;
  /** Whether the file has a classic cross-reference table (so it can be rewritten). */
  readonly classic: boolean;
  private readonly xrefOffset: number;
  private objectStreams = new Map<number, { data: Uint8Array; first: number; offsets: number[] }>();

  constructor(data: Uint8Array, options: { streams?: boolean } = {}) {
    this.bytes = data;
    const b = data;
    if (!(b.length > 8 && latin(b.subarray(0, 5)) === '%PDF-')) throw malformed('no PDF header');
    this.version = latin(b.subarray(5, 8));
    const start = lastIndexOf('startxref', b);
    if (start === null) throw malformed('no startxref');
    const first = new PDFLexer(b, start + 9).value();
    if (first?.t !== 'int') throw malformed('no cross-reference offset');
    this.xrefOffset = first.v;
    const offsets = new Map<number, number>();
    let trailer: PDFDict | null = null;
    let next: number | null = first.v;
    const seen = new Set<number>();
    let classic = true;
    while (next !== null && !seen.has(next)) {
      const at: number = next;
      seen.add(at);
      if (!(at >= 0 && at < b.length)) throw malformed('a cross-reference offset past the end of the file');
      const lexer = new PDFLexer(b, at);
      if (lexer.keyword() !== 'xref') {
        if (!options.streams) throw unsupported('a cross-reference stream (PDF 1.5 or later)');
        classic = false;
        const dict = this.readXRefStream(at, offsets);
        trailer ??= dict;
        const prev = dict.get('Prev');
        next = prev?.t === 'int' ? prev.v : null;
        continue;
      }
      for (;;) {
        const save = lexer.index;
        if (lexer.keyword() === 'trailer') break;
        lexer.index = save;
        const firstNumber = lexer.value(), count = lexer.value();
        // Each entry is 20 bytes, so no honest count exceeds the file's length over 20.
        if (firstNumber?.t !== 'int' || count?.t !== 'int' || firstNumber.v < 0 || count.v < 0 || count.v > b.length / 20 || firstNumber.v > 10_000_000) {
          throw malformed('a damaged cross-reference table');
        }
        lexer.skipWhitespace();
        for (let i = 0; i < count.v; i++) {
          const entry = lexer.index + i * 20;
          if (entry + 18 > b.length) throw malformed('a truncated cross-reference table');
          if (b[entry + 17] !== 0x6e || offsets.has(firstNumber.v + i)) continue;
          const digits = latin(b.subarray(entry, entry + 10));
          if (!/^\d{10}$/.test(digits) || Number(digits) >= b.length) throw malformed('a cross-reference entry out of range');
          offsets.set(firstNumber.v + i, Number(digits));
        }
        lexer.index += count.v * 20;
      }
      const dict = lexer.value();
      if (dict?.t !== 'dict') throw malformed('no trailer');
      trailer ??= dict.v;
      if (dict.v.has('XRefStm')) {
        if (!options.streams) throw unsupported('a hybrid cross-reference stream');
        const stm = dict.v.get('XRefStm');
        if (stm?.t === 'int') this.readXRefStream(stm.v, offsets);
      }
      const prev = dict.v.get('Prev');
      next = prev?.t === 'int' ? prev.v : null;
    }
    if (!trailer) throw malformed('no trailer');
    this.offsets = offsets;
    this.trailer = trailer;
    this.classic = classic;
  }

  /** A cross-reference stream at `at`: its entries into `offsets`/`compressed`; returns its dictionary. */
  private readXRefStream(at: number, offsets: Map<number, number>): PDFDict {
    const lexer = new PDFLexer(this.bytes, at);
    if (lexer.value()?.t !== 'int' || lexer.value()?.t !== 'int' || lexer.keyword() !== 'obj') throw malformed("a cross-reference stream that isn't an object");
    const value = lexer.value();
    const dict = asDict(value);
    if (!dict || asName(dict.get('Type')) !== 'XRef') throw malformed('a damaged cross-reference stream');
    const stream = this.streamAfter(lexer, dict);
    const data = PDFFile.decode({ value: value!, stream });
    if (!data) throw unsupported('a cross-reference stream filter');
    const w = (asArray(dict.get('W')) ?? []).map((x) => asNumber(x) ?? 0);
    if (w.length !== 3) throw malformed('a cross-reference stream without widths');
    const size = asNumber(dict.get('Size')) ?? 0;
    const index = (asArray(dict.get('Index')) ?? [V.int(0), V.int(size)]).map((x) => asNumber(x) ?? 0);
    const width = w[0] + w[1] + w[2];
    let p = 0;
    const field = (n: number, fallback: number) => {
      if (n === 0) return fallback;
      let v = 0;
      for (let k = 0; k < n; k++) v = v * 256 + data[p++];
      return v;
    };
    for (let s = 0; s + 1 < index.length; s += 2) {
      for (let i = 0; i < index[s + 1]; i++) {
        if (p + width > data.length) throw malformed('a truncated cross-reference stream');
        const type = field(w[0], 1), a = field(w[1], 0), c = field(w[2], 0);
        const number = index[s] + i;
        if (offsets.has(number) || this.compressed.has(number)) continue;
        if (type === 1) offsets.set(number, a);
        else if (type === 2) this.compressed.set(number, { stream: a, index: c });
      }
    }
    return dict;
  }

  /** The bytes of the stream whose dictionary the lexer has just read. */
  private streamAfter(lexer: PDFLexer, dict: PDFDict): Uint8Array {
    if (lexer.keyword() !== 'stream') throw malformed('a stream object without a stream');
    const b = this.bytes;
    if (lexer.index < b.length && b[lexer.index] === 0x0d) lexer.index++;
    if (lexer.index < b.length && b[lexer.index] === 0x0a) lexer.index++;
    let length: number;
    const l = dict.get('Length');
    if (l?.t === 'int') length = l.v;
    else if (l?.t === 'ref') {
      const v = this.header(l.n).value;
      if (v.t !== 'int') throw malformed("a stream length that isn't a number");
      length = v.v;
    } else throw malformed('a stream without a length');
    if (!(length >= 0 && length <= b.length - lexer.index)) throw malformed('a stream runs past the end of the file');
    return b.subarray(lexer.index, lexer.index + length);
  }

  /** Every object number the file lists. */
  get objectNumbers(): number[] {
    return [...new Set([...this.offsets.keys(), ...this.compressed.keys()])];
  }

  object(number: number): PDFObject {
    const inStream = this.compressed.get(number);
    if (inStream && !this.offsets.has(number)) return { value: this.fromObjectStream(inStream.stream, inStream.index), stream: null };
    const { value, lexer } = this.header(number);
    const save = lexer.index;
    if (lexer.keyword() !== 'stream') { lexer.index = save; return { value, stream: null }; }
    lexer.index = save;
    return { value, stream: this.streamAfter(lexer, asDict(value) ?? new PDFDict()) };
  }

  private fromObjectStream(number: number, index: number): PDFValue {
    let s = this.objectStreams.get(number);
    if (!s) {
      const object = this.object(number);
      const data = PDFFile.decode(object);
      const dict = asDict(object.value);
      if (!data || !dict) throw malformed(`object stream ${number} can't be read`);
      const n = asNumber(dict.get('N')) ?? 0, first = asNumber(dict.get('First')) ?? 0;
      const lexer = new PDFLexer(data, 0);
      const offsets: number[] = [];
      for (let i = 0; i < n; i++) {
        lexer.value();
        const offset = lexer.value();
        offsets.push(offset?.t === 'int' ? offset.v : 0);
      }
      s = { data, first, offsets };
      this.objectStreams.set(number, s);
    }
    if (index >= s.offsets.length) throw malformed(`object ${number} is missing from its stream`);
    return new PDFLexer(s.data, s.first + s.offsets[index]).value() ?? V.null;
  }

  /** An object's value, and the lexer just after it. */
  private header(number: number): { value: PDFValue; lexer: PDFLexer } {
    const offset = this.offsets.get(number);
    if (offset === undefined) {
      const inStream = this.compressed.get(number);
      if (inStream) return { value: this.fromObjectStream(inStream.stream, inStream.index), lexer: new PDFLexer(new Uint8Array(0), 0) };
      throw malformed(`object ${number} is missing`);
    }
    const lexer = new PDFLexer(this.bytes, offset);
    if (lexer.value()?.t !== 'int' || lexer.value()?.t !== 'int' || lexer.keyword() !== 'obj') {
      throw malformed(`object ${number} doesn't start where the table says`);
    }
    return { value: lexer.value() ?? V.null, lexer };
  }

  /** Follows a reference (one level); other values come back as they are. */
  resolve(value: PDFValue | null | undefined): PDFValue | null {
    if (value?.t === 'ref') return this.object(value.n).value;
    return value ?? null;
  }

  /** The object numbers of the pages, in order, with each page's inherited MediaBox. */
  pages(): { number: number; mediaBox: number[]; dict: PDFDict }[] {
    const root = this.trailer.get('Root');
    const catalog = root?.t === 'ref' ? asDict(this.object(root.n).value) : null;
    const pagesRoot = catalog?.get('Pages');
    if (!catalog || pagesRoot?.t !== 'ref') throw malformed('no page tree');
    const result: { number: number; mediaBox: number[]; dict: PDFDict }[] = [];
    const visited = new Set<number>();
    const walk = (number: number, inherited: number[] | null) => {
      if (visited.has(number)) return;
      visited.add(number);
      const node = asDict(this.object(number).value);
      if (!node) return;
      const own = asArray(this.resolve(node.get('MediaBox')));
      const box = own ? own.map(asNumber).filter((x): x is number => x !== null) : inherited;
      if (box && box.length !== 4) throw malformed(`page ${number}'s MediaBox isn't four numbers`);
      if (asName(node.get('Type')) === 'Pages') {
        for (const kid of asArray(node.get('Kids')) ?? []) if (kid.t === 'ref') walk(kid.n, box);
      } else {
        result.push({ number, mediaBox: box ?? [0, 0, 612, 792], dict: node });
      }
    };
    walk(pagesRoot.n, null);
    return result;
  }

  /** The decoded bytes of a stream (FlateDecode or none, with PNG predictors); null for filters this reader doesn't decode. */
  static decode(object: PDFObject): Uint8Array | null {
    const stream = object.stream;
    if (!stream) return null;
    const dict = asDict(object.value);
    const filter = dict?.get('Filter');
    const filters = filter?.t === 'array' ? filter.v.map(asName).filter((n): n is string => n !== null) : filter?.t === 'name' ? [filter.v] : [];
    if (filters.length === 0) return stream;
    if (filters.length !== 1 || filters[0] !== 'FlateDecode' || stream.length <= 2) return null;
    let data: Uint8Array;
    // zlib: a two-byte header, raw DEFLATE, a checksum that isn't needed.
    try { data = inflateSync(stream.subarray(2)); } catch { return null; }
    const parms = asDict(dict?.get('DecodeParms'));
    const predictor = asNumber(parms?.get('Predictor')) ?? 1;
    if (predictor >= 10) {
      const columns = asNumber(parms?.get('Columns')) ?? 1;
      data = unpredictPNG(data, columns);
    }
    return data;
  }

  // MARK: Writing

  /** Writes a new classic PDF: `version`, every object (unchanged ones byte for byte, `replace`d ones re-serialised,
   *  `add`ed ones appended) and a fresh cross-reference table. */
  rewritten(version: string, replace: Map<number, PDFValue>, add: PDFValue[], trailer: (firstNew: number) => PDFDict): Uint8Array {
    if (!this.classic || this.compressed.size) throw unsupported('a cross-reference stream (PDF 1.5 or later)');
    const out = new ByteWriter();
    out.write(ascii(`%PDF-${version}\n%`));
    out.write(new Uint8Array([0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));
    // Each object's bytes run from its offset to the next object (or the cross-reference table).
    const sorted = [...this.offsets.entries()].sort((a, b) => a[1] - b[1]);
    const ranges = new Map<number, [number, number]>();
    sorted.forEach(([number, offset], i) => {
      const end = i + 1 < sorted.length ? sorted[i + 1][1] : Math.min(this.xrefOffset, this.bytes.length);
      ranges.set(number, [offset, Math.max(offset, end)]);
    });
    const firstNew = Math.max(0, ...this.offsets.keys()) + 1;
    const newOffsets = new Map<number, number>();
    for (const number of [...this.offsets.keys()].sort((a, b) => a - b)) {
      newOffsets.set(number, out.length);
      const value = replace.get(number);
      if (value) {
        out.write(ascii(`${number} 0 obj\n`)); out.write(serialize(value)); out.write(ascii('\nendobj\n'));
      } else {
        const range = ranges.get(number);
        if (!range) continue;
        let end = range[1];
        while (end > range[0] && (this.bytes[end - 1] === 0x0a || this.bytes[end - 1] === 0x0d || this.bytes[end - 1] === 0x20)) end--;
        out.write(this.bytes.subarray(range[0], end)); out.write(new Uint8Array([0x0a]));
      }
    }
    add.forEach((value, i) => {
      newOffsets.set(firstNew + i, out.length);
      out.write(ascii(`${firstNew + i} 0 obj\n`)); out.write(serialize(value)); out.write(ascii('\nendobj\n'));
    });
    const size = firstNew + add.length;
    const xref = out.length;
    let table = `xref\n0 ${size}\n0000000000 65535 f \n`;
    for (let number = 1; number < size; number++) {
      const offset = newOffsets.get(number);
      table += offset !== undefined ? `${String(offset).padStart(10, '0')} 00000 n \n` : '0000000000 65535 f \n';
    }
    out.write(ascii(table));
    const t = trailer(firstNew);
    t.set('Size', V.int(size));
    t.set('Prev', null);
    out.write(ascii('trailer\n')); out.write(serialize(V.dict(t))); out.write(ascii(`\nstartxref\n${xref}\n%%EOF\n`));
    return out.bytes();
  }
}

/** `PDFFile.serialize`. */
export function serialize(value: PDFValue): Uint8Array {
  const out = new ByteWriter();
  write(value, out);
  return out.bytes();
}

const hex2 = (c: number) => c.toString(16).toUpperCase().padStart(2, '0');
const NAME_ESCAPED = new Set([...'()<>[]{}/%#'].map((c) => c.charCodeAt(0)));

function write(value: PDFValue, out: ByteWriter): void {
  switch (value.t) {
    case 'null': out.write(ascii('null')); return;
    case 'bool': out.write(ascii(value.v ? 'true' : 'false')); return;
    case 'int': out.write(ascii(String(value.v))); return;
    case 'real': out.write(ascii(value.v.toFixed(4).replace(/\.?0+$/, ''))); return;
    case 'name': {
      let s = '/';
      for (const c of new TextEncoder().encode(value.v)) s += c > 0x20 && c < 0x7f && !NAME_ESCAPED.has(c) ? String.fromCharCode(c) : `#${hex2(c)}`;
      out.write(ascii(s));
      return;
    }
    case 'string': {
      if (value.hex) { out.write(ascii('<' + [...value.bytes].map(hex2).join('') + '>')); return; }
      const bytes: number[] = [0x28];
      for (const c of value.bytes) {
        if (c === 0x28 || c === 0x29 || c === 0x5c) bytes.push(0x5c, c);
        else if (c === 0x0a) bytes.push(0x5c, 0x6e);
        else if (c === 0x0d) bytes.push(0x5c, 0x72);
        else bytes.push(c);
      }
      bytes.push(0x29);
      out.write(new Uint8Array(bytes));
      return;
    }
    case 'array':
      out.write(ascii('['));
      value.v.forEach((item, i) => { if (i > 0) out.write(ascii(' ')); write(item, out); });
      out.write(ascii(']'));
      return;
    case 'dict':
      out.write(ascii('<<'));
      for (const [key, item] of value.v.pairs) { write(V.name(key), out); out.write(ascii(' ')); write(item, out); out.write(ascii(' ')); }
      out.write(ascii('>>'));
      return;
    case 'ref': out.write(ascii(`${value.n} ${value.g} R`)); return;
  }
}

class ByteWriter {
  private chunks: Uint8Array[] = [];
  length = 0;
  write(bytes: Uint8Array): void { this.chunks.push(bytes); this.length += bytes.length; }
  bytes(): Uint8Array {
    const out = new Uint8Array(this.length);
    let at = 0;
    for (const c of this.chunks) { out.set(c, at); at += c.length; }
    return out;
  }
}

/** Undoes PNG row predictors (Predictor ≥ 10), one filter byte per row. */
function unpredictPNG(data: Uint8Array, columns: number): Uint8Array {
  const row = columns + 1;
  const rows = Math.floor(data.length / row);
  const out = new Uint8Array(rows * columns);
  for (let r = 0; r < rows; r++) {
    const type = data[r * row];
    for (let c = 0; c < columns; c++) {
      const x = data[r * row + 1 + c];
      const left = c > 0 ? out[r * columns + c - 1] : 0;
      const up = r > 0 ? out[(r - 1) * columns + c] : 0;
      const upLeft = r > 0 && c > 0 ? out[(r - 1) * columns + c - 1] : 0;
      let v = x;
      if (type === 1) v = x + left;
      else if (type === 2) v = x + up;
      else if (type === 3) v = x + ((left + up) >> 1);
      else if (type === 4) {
        const p = left + up - upLeft, pa = Math.abs(p - left), pb = Math.abs(p - up), pc = Math.abs(p - upLeft);
        v = x + (pa <= pb && pa <= pc ? left : pb <= pc ? up : upLeft);
      }
      out[r * columns + c] = v & 0xff;
    }
  }
  return out;
}
